import { StrictLocalMsg } from "@faire/localization/react/StrictLocalMsg";
import { FormHelper } from "@faire/slate/private/form/formTexts";
import React from "react";

type IFormHelperProps = Omit<React.ComponentProps<typeof FormHelper>, "id"> & {
  helperId?: string;
};

type IProps = IFormHelperProps & ICharCounterProps;

export const HelperWithCharCounter: React.FC<IProps> = ({
  characterCount,
  characterLimit,
  disabled,
  error,
  children,
  helperId,
}) => {
  return (
    <span className="flex items-start justify-between gap-(--slate-spacing-element-large)">
      <FormHelper id={helperId} error={error} disabled={disabled} gap={0}>
        {children}
      </FormHelper>
      {/* lowcost hack to right align the counter even when there isn't a helper or error text  */}
      <span />
      <CharCounter
        disabled={disabled}
        characterLimit={characterLimit}
        characterCount={characterCount}
      />
    </span>
  );
};

interface ICharCounterProps {
  characterCount: number;
  characterLimit?: number;
  disabled?: boolean;
}

const CharCounter: React.FC<ICharCounterProps> = ({
  characterCount,
  characterLimit,
  disabled,
}) => {
  const limitReached = characterCount === characterLimit;

  if (characterLimit === undefined) {
    return null;
  }

  return (
    <>
      <span
        className="fs-form-field__text type-fs-label"
        aria-hidden
        data-disabled={disabled}
      >
        <StrictLocalMsg
          id="kejVtZ"
          defaultMessage="{characterCountNumber}/{characterLimitNumber}"
          values={{
            characterCountNumber: characterCount,
            characterLimitNumber: characterLimit,
          }}
          description={{
            text: "Text area character counter: the number of characters in the text area / the limit of characters (e.g. 100/200)",
            img: "https://cdn.faire.com/fastly/3692c93443a9ed77d13f2a55b3354a07d802c32ba7d17699f58603af49a1ca75.png",
          }}
        />
      </span>
      {limitReached && !disabled ? (
        <span className="sr-only" aria-live="polite">
          <StrictLocalMsg
            id="jwr4Fd"
            defaultMessage="{characterLimitNumber, plural, one {Limit reached. You can only use # character in this field.} other {Limit reached. You can only use # characters in this field.}}"
            values={{ characterLimitNumber: characterLimit }}
            description={{
              text: "Assistive note explaining the field has a limit of characters and it has been reached",
            }}
          />
        </span>
      ) : null}
    </>
  );
};
